'use client';

import type { HTMLAttributes } from 'react';
import { createElement } from 'react';
import { cx } from '../utils/cx';
import { mergePresetClasses } from '../utils/mergePresetClasses';

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  tone?: 'primary' | 'secondary' | 'tertiary';
  /**
   * Explicit text alignment. Intentionally has NO default: when omitted the
   * heading emits no `text-*` alignment utility and inherits alignment from its
   * container.
   *
   * A hardcoded `text-left` default used to be emitted here, which (a) beat the
   * consumer's own base `text-center` — Tailwind resolves conflicts by
   * stylesheet order, not by order within the `class` attribute — and (b) was a
   * physical utility that ignored `direction`, pinning RTL headings to the left
   * edge. Inheriting is both override-friendly and RTL-correct.
   */
  align?: 'left' | 'center' | 'right';
  truncate?: boolean;
  text?: string;
}

const sizeByLevel: Record<NonNullable<HeadingProps['level']>, string> = {
  1: 'text-[1.875rem] font-semibold leading-tight tracking-[-0.03em] sm:text-[2.25rem]',
  2: 'text-2xl font-semibold leading-tight tracking-[-0.025em] sm:text-3xl',
  3: 'text-xl font-semibold leading-tight tracking-[-0.02em]',
  4: 'text-lg font-semibold leading-tight',
  5: 'text-base font-medium leading-tight',
  6: 'text-sm font-medium leading-tight',
};

const toneMap: Record<NonNullable<HeadingProps['tone']>, string> = {
  primary: 'text-primary',
  secondary: 'text-secondary',
  tertiary: 'text-tertiary',
};

const alignMap: Record<NonNullable<HeadingProps['align']>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function resolveHeadingTag(
  level: NonNullable<HeadingProps['level']>,
  as: HeadingProps['as'],
): 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' {
  if (as) return as;
  return `h${level}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export function Heading({
  level = 2,
  as,
  tone = 'primary',
  align,
  truncate = false,
  text,
  className,
  children,
  ...rest
}: HeadingProps) {
  const tag = resolveHeadingTag(level, as);
  const content = children ?? text ?? null;

  // Component defaults are the base layer; `className` is an explicit override.
  // Tailwind resolves conflicting utilities by stylesheet order, not attribute
  // order, so conflicting base utilities must be dropped before rendering —
  // same contract as `Box` and `Card`.
  const presetClasses = cx(
    toneMap[tone],
    align ? alignMap[align] : null,
    sizeByLevel[level],
    truncate && 'truncate',
  );

  return createElement(
    tag,
    {
      ...rest,
      className: cx(mergePresetClasses(presetClasses, className)),
    },
    content,
  );
}

Heading.displayName = 'Heading';